import { useEffect, useState } from 'react';
import {
  Button,
  Badge,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@rnd-payplus/alloy-react';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentTenantId } from '../utils/auth';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';

interface SapPaymentRow {
  id: string;
  tenant_id: string;
  payment_run_id: string;
  document_number: string | null;
  company_code: string | null;
  vendor_id: string | null;
  amount: string | null;
  currency: string | null;
  payment_date: string | null;
  status: string;
  created_at: string;
}

const SAPPaymentsPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<SapPaymentRow[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);

  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const res = await fetch('/api/sap/payments');
      if (!res.ok) {
        throw new Error(`Failed to load payments (${res.status})`);
      }
      setPayments(await res.json());
    } catch (err) {
      setPayments([]);
      toast.error(err instanceof Error ? err.message : 'Failed to load payments');
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const fileContent = e.target?.result as string;

          const response = await fetch('/api/sap/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileName: file.name,
              fileContent: fileContent,
              tenantId: getCurrentTenantId(),
            }),
          });

          const data = await response.json();

          if (data.success) {
            setResult(data);
            toast.success('File processed successfully!');
            loadPayments();
          } else {
            setError(data.error || 'Failed to process file');
            toast.error(data.error || 'Failed to process file');
          }
        } catch (err: any) {
          setError(err.message || 'Failed to upload file');
          toast.error(err.message || 'Failed to upload file');
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file');
        toast.error('Failed to read file');
        setUploading(false);
      };

      reader.readAsText(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
      toast.error(err.message || 'Failed to upload file');
      setUploading(false);
    }
  };

  const totalPayments = payments.length;
  const totalAmount = payments.reduce(
    (sum, p) => sum + (p.amount ? parseFloat(p.amount) : 0),
    0
  );
  const formattedAmount = totalAmount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const uniqueVendors = new Set(payments.map(p => p.vendor_id).filter(Boolean)).size;
  const uniqueRuns = new Set(payments.map(p => p.payment_run_id)).size;

  usePageShell({
    title: 'SAP Payment File Upload',
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'SAP Payments' },
    ],
  });

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total" value={totalPayments} subtitle="Payments" />
        <KPICard title="Total Amount" value={formattedAmount} subtitle="All Currencies" />
        <KPICard title="Vendors" value={uniqueVendors} subtitle="Unique" />
        <KPICard title="Payment Runs" value={uniqueRuns} subtitle="Unique" />
      </div>

      <div className="mb-6">
        <h2 className="text-base font-medium mb-4">Upload SAP F110/F111 File</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Upload a SAP F110 XML payment file or a flat IDoc text file. Records will be parsed and saved to the dashboard database.
          </p>

          <div className="flex gap-3 items-center">
            <input
              type="file"
              accept=".xml,.txt,.idoc"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="sap-file-upload"
            />
            <label htmlFor="sap-file-upload">
              <Button variant="outline" asChild>
                <span>Choose File</span>
              </Button>
            </label>

            {file && (
              <span className="text-sm">
                Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)
              </span>
            )}

            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? 'Processing...' : 'Upload & Process'}
            </Button>
          </div>
      </div>

      {uploading && (
        <div className="flex justify-center items-center p-8">
          <Spinner />
          <span className="ml-3">Processing SAP file...</span>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-destructive bg-destructive/10 p-4 text-destructive text-sm">
          {error}
        </div>
      )}

      {result && (
        <>
          <div className="mb-4 rounded-md border border-green-500 bg-green-50 p-4 text-green-800 text-sm dark:bg-green-950 dark:text-green-300">
            File processed successfully!
          </div>

          <div className="mb-5">
            <h2 className="text-base font-medium leading-6 mb-4">Processing Summary</h2>
            <div className="flex gap-8">
                <div>
                  <p className="text-sm text-muted-foreground">Payments Parsed</p>
                  <p className="text-3xl font-bold">{result.parsed || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payments Routed</p>
                  <p className="text-3xl font-bold text-green-600">{result.routed || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Errors</p>
                  <p className="text-3xl font-bold text-red-600">
                    {(result.parsed || 0) - (result.routed || 0)}
                  </p>
                </div>
            </div>
          </div>

          {result.results && result.results.length > 0 && (
            <div className="mb-8">
              <h2 className="text-base font-medium leading-6 mb-4">Just Uploaded</h2>
              <div className="rounded-md border overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document Number</TableHead>
                      <TableHead>Database ID</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.results.slice(0, 50).map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs">{r.payment}</TableCell>
                        <TableCell className="font-mono text-xs">{r.id}</TableCell>
                        <TableCell>
                          <Badge
                            variant={r.status === 'saved_to_db' ? 'default' : 'destructive'}
                          >
                            {r.status === 'saved_to_db' ? 'Saved' : r.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </>
      )}

      <div>
        <h2 className="text-base font-medium leading-6 mb-4">All SAP Payments</h2>
        {loadingPayments ? (
          <div className="flex justify-center items-center p-8">
            <Spinner />
            <span className="ml-3">Loading payments...</span>
          </div>
        ) : payments.length === 0 ? (
          <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
            No SAP payments yet. Upload a file above to populate this table.
          </div>
        ) : (
          <div className="rounded-md border overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Run ID</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.payment_run_id}</TableCell>
                    <TableCell className="font-mono text-xs">{p.document_number || '-'}</TableCell>
                    <TableCell>{p.company_code || '-'}</TableCell>
                    <TableCell>{p.vendor_id || '-'}</TableCell>
                    <TableCell className="text-right">{p.amount ?? '-'}</TableCell>
                    <TableCell>{p.currency || '-'}</TableCell>
                    <TableCell>{p.payment_date || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={p.status === 'pending' ? 'secondary' : 'default'}>
                        {p.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SAPPaymentsPage;
